/**
 * Color Override Widget
 *
 * Debug widget that allows overriding background colors of all UI panels
 * and the Potree viewer background. Useful for testing themes and contrast.
 *
 * Targets elements via `data-panel` attributes added to React components
 * and the `#olContainer` element for the viewer background.
 */

import { DebugWidget, createWidgetContainer } from './DebugOverlay';

interface ColorOverrideEntry {
  label: string;
  /** CSS selector to target */
  selector: string;
  /** Default color value to show in the picker */
  defaultColor: string;
}

/** Panels whose background color can be overridden */
const PANEL_OVERRIDES: ColorOverrideEntry[] = [
  {
    label: 'Header',
    selector: '[data-panel="header"]',
    defaultColor: '#f1f3f3',
  },
  {
    label: 'Left Panel',
    selector: '[data-panel="left"]',
    defaultColor: '#e6e6e6',
  },
  {
    label: 'Right Panel',
    selector: '[data-panel="right"]',
    defaultColor: '#e6e6e6',
  },
  {
    label: 'Tools Bar',
    selector: '[data-panel="tools-bar"]',
    defaultColor: '#d2d2d2',
  },
  {
    label: 'Viewer Background',
    selector: '#olContainer',
    defaultColor: '#cdcdcd',
  },
];

export class ColorOverrideWidget implements DebugWidget {
  name = 'color-override';
  container: HTMLElement;
  private styleEl: HTMLStyleElement;
  private overrides: Map<string, string> = new Map();
  private checkboxes: Map<string, HTMLInputElement> = new Map();

  constructor() {
    this.container = createWidgetContainer('🎨 Color Overrides');

    // Create the injected style element
    this.styleEl = document.createElement('style');
    this.styleEl.id = 'potree-debug-color-overrides';
    document.head.appendChild(this.styleEl);

    const content = document.createElement('div');
    content.style.cssText = 'display: flex; flex-direction: column; gap: 8px;';

    for (const entry of PANEL_OVERRIDES) {
      const row = this.createColorRow(entry);
      content.appendChild(row);
    }

    // Reset button
    const resetBtn = document.createElement('button');
    resetBtn.textContent = 'Reset All';
    resetBtn.style.cssText = `
      margin-top: 8px;
      padding: 4px 12px;
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.3);
      border-radius: 4px;
      color: #fff;
      cursor: pointer;
      font-family: inherit;
      font-size: inherit;
      width: 100%;
    `;
    resetBtn.addEventListener('click', () => this.resetAll());
    resetBtn.addEventListener('mouseenter', () => {
      resetBtn.style.background = 'rgba(255, 255, 255, 0.2)';
    });
    resetBtn.addEventListener('mouseleave', () => {
      resetBtn.style.background = 'rgba(255, 255, 255, 0.1)';
    });
    content.appendChild(resetBtn);

    this.container.appendChild(content);
  }

  private createColorRow(entry: ColorOverrideEntry): HTMLElement {
    const row = document.createElement('div');
    row.style.cssText = `
      display: flex;
      align-items: center;
      gap: 8px;
    `;

    // Enable checkbox
    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.style.cssText = 'cursor: pointer; width: 14px; height: 14px; flex-shrink: 0;';
    this.checkboxes.set(entry.label, checkbox);

    // Label
    const label = document.createElement('span');
    label.textContent = entry.label;
    label.style.cssText = 'flex: 1; user-select: none; font-size: 11px;';

    // Color picker
    const colorInput = document.createElement('input');
    colorInput.type = 'color';
    colorInput.value = entry.defaultColor;
    colorInput.style.cssText = `
      width: 32px;
      height: 24px;
      border: 1px solid rgba(255, 255, 255, 0.3);
      border-radius: 3px;
      cursor: pointer;
      background: transparent;
      padding: 0;
      flex-shrink: 0;
    `;

    // Hex display
    const hexDisplay = document.createElement('span');
    hexDisplay.textContent = entry.defaultColor;
    hexDisplay.style.cssText = 'font-size: 10px; color: rgba(255, 255, 255, 0.5); width: 60px; text-align: right;';

    const applyOverride = () => {
      if (checkbox.checked) {
        this.overrides.set(entry.label, colorInput.value);
      } else {
        this.overrides.delete(entry.label);
      }
      hexDisplay.textContent = colorInput.value;
      this.applyStyles();
    };

    checkbox.addEventListener('change', applyOverride);
    colorInput.addEventListener('input', () => {
      if (!checkbox.checked) {
        checkbox.checked = true;
      }
      applyOverride();
    });

    row.appendChild(checkbox);
    row.appendChild(label);
    row.appendChild(colorInput);
    row.appendChild(hexDisplay);

    return row;
  }

  private applyStyles(): void {
    let css = '';

    for (const entry of PANEL_OVERRIDES) {
      const color = this.overrides.get(entry.label);
      if (!color) continue;

      // Use !important to override both Tailwind classes and inline styles
      css += `${entry.selector} { background-color: ${color} !important; }\n`;
      // Also override in dark mode
      css += `.dark ${entry.selector} { background-color: ${color} !important; }\n`;
    }

    this.styleEl.textContent = css;
  }

  private resetAll(): void {
    this.overrides.clear();
    this.checkboxes.forEach((checkbox) => {
      checkbox.checked = false;
    });
    this.styleEl.textContent = '';
  }

  update(): void {
    // No periodic work needed — CSS selectors target data attributes directly
  }

  destroy(): void {
    this.resetAll();
    this.styleEl.remove();
  }
}
